/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState } from 'react'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import type { Workspace } from '../../types/workspace'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import { useWorkspace } from '../../context/useWorkspace'
import { requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import { useAuth } from '../../context/useAuth'
import { useNls } from '../../nls/nlsProvider'
import {
  IconButton,
  LeaveWorkspaceConfirmModal,
  Pressable,
  WorkspaceSwitcherDropdown,
  WorkspaceSwitcherRow,
} from '../Common'
import { leaveWorkspaceAndPurgeOutbox } from '../../services/memberService'
import { usePushBackHandler } from '../../context/BackStackContext'
import { isPersonalWorkspace, isTeamWorkspace } from '../../utils/workspaceKind'
import { isWorkspaceOwner, getWorkspaceOwnerIds } from '../../utils/workspaceOwners'
import { featureFlag } from '../../utils/featureFlags'
import { safeMemberLanding } from '../../services/workspaceSwitch/switchLanding'
import { roleLabelKey } from '../../utils/roleLabels'
import { MemberWorkspaceKindBadge } from './MemberWorkspaceKindBadge'
import { useLaunchPersonalWorkspace } from '../../hooks/useLaunchPersonalWorkspace'
import { useFamilyAccess } from '../../hooks/useFamilyAccess'

// Members' workspaces carry no color field (unlike helper summaries), so
// derive a stable hue from the id to keep avatars distinguishable.
// §8.0.5 palette is centralized in src/components/Common/avatarUtils.ts.
import { AVATAR_COLORS, colorFor } from '../Common/avatarUtils'
export { AVATAR_COLORS, colorFor }

export type MemberWorkspaceSwitcherProps = {
  workspaces: Workspace[]
  activeWorkspaceId: string | null
  activeRole: WorkspaceRole
  onSelect?: (workspace: Workspace) => void
  getRole?: (workspaceId: string) => WorkspaceRole
}

export const MemberWorkspaceSwitcher = ({
  workspaces,
  activeWorkspaceId,
  activeRole,
  onSelect,
}: MemberWorkspaceSwitcherProps) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { refreshWorkspaces, setAppMode } = useWorkspace()
  const { canSwitchToFamily } = useFamilyAccess()
  const [open, setOpen] = useState(false)
  usePushBackHandler(() => setOpen(false), open)
  const [leavingWorkspace, setLeavingWorkspace] = useState<Workspace | null>(null)

  const handleLeftWorkspace = async () => {
    if (!leavingWorkspace) return
    const leftId = leavingWorkspace.id
    setLeavingWorkspace(null)
    setOpen(false)
    if (refreshWorkspaces) await refreshWorkspaces()
    const remaining = workspaces.filter((entry) => entry.id !== leftId && !entry.isPreview)
    if (remaining.length > 0) {
      void requestWorkspaceSwitch({
        toWorkspaceId: remaining[0].id,
        source: 'MEMBERSHIP_ENDED',
        landing: safeMemberLanding({ workspace: remaining[0], tab: 'for-you' }),
      })
      if (setAppMode) setAppMode(remaining[0].kind ?? (isPersonalWorkspace(remaining[0]) ? 'PERSONAL' : 'TEAM'))
    } else {
      void requestWorkspaceSwitch({
        toWorkspaceId: null,
        source: 'MEMBERSHIP_ENDED',
      })
    }
  }

  const active = useMemo(
    () => workspaces.find((entry) => entry.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )

  const personalInMemberAppEnabled = featureFlag('personalInMemberApp')
  const { hasPersonal, activateExisting } = useLaunchPersonalWorkspace()
  const showCreatePersonalCta = personalInMemberAppEnabled && !hasPersonal && Boolean(currentUser)

  const handleSelect = (workspace: Workspace, close: () => void) => {
    close()
    if (onSelect) {
      onSelect(workspace)
    } else {
      void requestWorkspaceSwitch({
        toWorkspaceId: workspace.id,
        source: 'USER',
        landing: safeMemberLanding({ workspace, tab: 'for-you' }),
      })
      setAppMode?.(isPersonalWorkspace(workspace) ? 'PERSONAL' : 'TEAM')
    }
  }

  const renderWorkspaceRow = (workspace: Workspace, close: () => void) => {
    const isActive = workspace.id === activeWorkspaceId
    const canLeave =
      !isPersonalWorkspace(workspace) &&
      !workspace.isPreview &&
      !(isWorkspaceOwner(workspace, currentUser?.uid) && getWorkspaceOwnerIds(workspace).length <= 1)
    const personal = isPersonalWorkspace(workspace)
    return (
      <WorkspaceSwitcherRow
        key={workspace.id}
        id={workspace.id}
        name={workspace.name}
        isActive={isActive}
        logoUrl={workspace.branding?.logoUrl}
        accentColor={workspace.branding?.accentColor || colorFor(workspace.id)}
        icon={
          personal ? (
            <span
              className="h-7 w-7 rounded-xl flex items-center justify-center text-[var(--text-strong)] shrink-0"
              style={{ color: 'var(--accent)', background: 'var(--control)' }}
            >
              <PersonRoundedIcon fontSize="small" />
            </span>
          ) : undefined
        }
        badge={<MemberWorkspaceKindBadge variant={personal ? 'personal' : 'team'} />}
        action={
          canLeave ? (
            <IconButton
              variant="tertiary"
              tone="danger"
              size="sm"
              className="p-2 mr-1 rounded-lg text-[var(--muted)] hover:text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] transition shrink-0 text-sm"
              title={t('NLSInvitesLeaveWorkspaceAction')}
              label={`${t('NLSInvitesLeaveWorkspaceAction')} - ${workspace.name}`}
              data-testid={`leave-workspace-${workspace.id}`}
              icon={<LogoutRoundedIcon fontSize="inherit" />}
              onClick={(event) => {
                event.stopPropagation()
                setLeavingWorkspace(workspace)
              }}
            />
          ) : null
        }
        onClick={() => handleSelect(workspace, close)}
      />
    )
  }

  return (
    <>
      <WorkspaceSwitcherDropdown
        testId="member-workspace-switcher"
        triggerTestId="member-workspace-trigger"
        listTestId="member-workspace-list"
        triggerLabel={active ? `${active.name} - ${t(roleLabelKey(activeRole))}` : 'Select workspace'}
        triggerBrandName={active?.name}
        triggerLogoUrl={active?.branding?.logoUrl}
        triggerAccentColor={active?.branding?.accentColor || (active ? colorFor(active.id) : undefined)}
        triggerIcon={
          active && isPersonalWorkspace(active) ? (
            <span
              className="h-6 w-6 rounded-xl flex items-center justify-center text-[var(--text-strong)] shrink-0"
              style={{ color: 'var(--accent)', background: 'var(--control)' }}
            >
              <PersonRoundedIcon fontSize="small" />
            </span>
          ) : undefined
        }
        triggerMaxWidth="max-w-[240px]"
        dropdownWidth="w-[280px]"
        itemCount={workspaces.length}
        open={open}
        onOpenChange={setOpen}
      >
        {({ search, close }) => {
          const trimmed = search.trim().toLowerCase()
          const visible = trimmed
            ? workspaces.filter((entry) => entry.name.toLowerCase().includes(trimmed))
            : workspaces
          const personalWorkspaces = personalInMemberAppEnabled ? visible.filter(isPersonalWorkspace) : []
          const teamWorkspaces = visible.filter(isTeamWorkspace)

          return (
            <>
              {(personalInMemberAppEnabled && personalWorkspaces.length > 0) || showCreatePersonalCta ? (
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold tracking-[0.08em] uppercase text-[var(--muted)]">
                  My workspaces
                </div>
              ) : null}
              {personalWorkspaces.map((ws) => renderWorkspaceRow(ws, close))}
              {showCreatePersonalCta ? (
                <div className="flex items-center rounded-xl transition text-[var(--text)] hover:bg-[var(--surface-hover)]">
                  <Pressable
                    className="flex items-center gap-3 px-3 h-12 text-left flex-1 min-w-0"
                    onClick={() => {
                      if (activateExisting()) return
                      window.location.href = '/personal'
                    }}
                    aria-label="Create Personal Space"
                  >
                    <span
                      className="h-7 w-7 rounded-xl flex items-center justify-center text-[var(--text-strong)] shrink-0"
                      style={{ color: 'var(--accent)', background: 'var(--control)' }}
                      aria-hidden="true"
                    >
                      <PersonRoundedIcon fontSize="small" />
                    </span>
                    <span className="flex-1 truncate text-sm">Create Personal Space</span>
                    <span className="text-[11px] font-medium text-[var(--muted)] shrink-0">Get started</span>
                  </Pressable>
                </div>
              ) : null}
              {personalInMemberAppEnabled && (personalWorkspaces.length > 0 || showCreatePersonalCta) && teamWorkspaces.length > 0 ? (
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold tracking-[0.08em] uppercase text-[var(--muted)]">
                  Teams
                </div>
              ) : null}
              {teamWorkspaces.map((ws) => renderWorkspaceRow(ws, close))}
              {canSwitchToFamily(activeWorkspaceId ?? '') ? (
                <div className="pt-1 border-t border-[var(--border)] mt-1">
                  <div className="flex items-center rounded-xl transition text-[var(--text)] hover:bg-[var(--surface-hover)]">
                    <Pressable
                      className="flex items-center gap-3 px-3 h-12 text-left flex-1 min-w-0"
                      onClick={() => {
                        close()
                        if (typeof window !== 'undefined') {
                          window.history.replaceState({ fromWorkspaceId: activeWorkspaceId }, '', '/family')
                          window.dispatchEvent(new PopStateEvent('popstate'))
                        }
                      }}
                      aria-label={t('NLSMemberMenuFamilyView')}
                    >
                      <span
                        className="h-7 w-7 rounded-xl flex items-center justify-center text-[var(--text-strong)] shrink-0"
                        style={{ color: 'var(--accent)', background: 'var(--control)' }}
                        aria-hidden="true"
                      >
                        <FamilyRestroomRoundedIcon fontSize="small" />
                      </span>
                      <span className="flex-1 truncate text-sm">{t('NLSMemberMenuFamilyView')}</span>
                      <span className="text-[11px] font-medium text-[var(--muted)] shrink-0">Switch</span>
                    </Pressable>
                  </div>
                </div>
              ) : null}
              {visible.length === 0 ? (
                <p className="text-center text-xs text-[var(--muted)] py-6">No workspaces match.</p>
              ) : null}
            </>
          )
        }}
      </WorkspaceSwitcherDropdown>

      {leavingWorkspace ? (
        <LeaveWorkspaceConfirmModal
          open={Boolean(leavingWorkspace)}
          workspaceId={leavingWorkspace.id}
          workspaceName={leavingWorkspace.name}
          onClose={() => setLeavingWorkspace(null)}
          onConfirm={async () => {
            await leaveWorkspaceAndPurgeOutbox({ workspaceId: leavingWorkspace.id })
          }}
          onLeft={handleLeftWorkspace}
          onNavigateToOwners={() => {
            setLeavingWorkspace(null)
            setOpen(false)
            window.location.hash = '#/team'
          }}
        />
      ) : null}
    </>
  )
}
